import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'

import { process } from '@progress/kendo-data-query'

import { isPending } from 'global/redux/toolkit/api'
import { SearchFieldProps, useSearchFieldLogic } from 'global/components/lib/searchField/SearchField'
import { BDSGridPagerConfig, BDSGridSortConfig } from 'global/types/dataTables/dataTables'
import useTablePeriodicCheck from 'global/lib/useTablePeriodicCheck/useTablePeriodicCheck'
import { formatDate } from 'global/lib/datetime'
import { config } from 'global/lib/config'

import { ColumnsConfig } from 'admin/redux/types/dataTables'
import { useAppDispatch, useAppSelector } from 'admin/redux/toolkit/hooks'
import { listAccessTokens, resetAccessTokens, reset as resetAdminSlice } from 'admin/redux/features/admin/adminSlice'
import {
  update as updateAccountsTable,
  reset as resetAccountsTable
} from 'admin/redux/features/dataTables/deletedAccessTokens/deletedAccessTokensSlice'
import { ListAccessToken, ModifiedListAccessToken } from 'admin/redux/types/scans'

export type InProgress = boolean

export interface TableConfig {
  columns: { [key: string]: string }
  columnsConfig: ColumnsConfig
  isLoaded: boolean
  inProgress: boolean
  pageConfig: BDSGridPagerConfig
  searchFieldConfig: SearchFieldProps
  sortConfig: BDSGridSortConfig | {}
  tableData: {
    total: number
    data: ModifiedListAccessToken[]
  }
}

export type AllAccountLogic = [TableConfig]

// Empty accessTokenProduct results in deleted tokens from Admin API for all products
const accessTokenProduct = ''
const SEARCH_FIELD_ID = 'scans-table-search'

export default function useAllAccountLogic(): AllAccountLogic {
  const dispatch = useAppDispatch()
  const { accessTokens, accessTokenTable, listAccessTokenInProgress, loadedAccessTokensOffsets } = useAppSelector(
    _stores => ({
      accessTokens: _stores.admin.accessTokens,
      accessTokenTable: _stores.dataTables.deletedAccessTokens,
      listAccessTokenInProgress: isPending(_stores.admin.listAccessTokensApiStatus),
      loadedAccessTokensOffsets: _stores.admin.loadedAccessTokensOffsets
    })
  )
  const [state, setState] = useReducer((_state: any, newState: any) => ({ ..._state, ...newState }), {
    searchString: ''
  })

  const [initTableRefresh] = useTablePeriodicCheck()

  // init
  useEffect(() => {
    initTableRefresh(tableRefresh)

    dispatch(listAccessTokens({ product: accessTokenProduct }))

    // unmount
    return () => {
      dispatch(resetAccessTokens(true))
      dispatch(resetAccountsTable())
      dispatch(resetAdminSlice())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const resetTable = useCallback(() => {
    dispatch(resetAccessTokens())
    dispatch(listAccessTokens({ product: accessTokenProduct }))
  }, [dispatch])

  const tableRefresh = useCallback(() => {
    if (!listAccessTokenInProgress) {
      resetTable()
    }
  }, [resetTable, listAccessTokenInProgress])

  const updateTableData = useCallback(
    (changes: any = {}) => {
      dispatch(
        updateAccountsTable({
          ...changes,
          skip: 0
        })
      )

      initTableRefresh(tableRefresh)
      resetTable()
    },
    [dispatch, resetTable, initTableRefresh, tableRefresh]
  )

  const [debouncedOnChange, validateSearchString] = useSearchFieldLogic(
    (search: string) => {
      updateTableData({ search: search.trim() })
      setState({ searchString: search.trim() })
    },
    listAccessTokenInProgress,
    SEARCH_FIELD_ID
  )

  const tableData = useMemo(() => {
    const { skip, take } = accessTokenTable

    const { data } = process(
      (accessTokens?.data || []).map((report: ListAccessToken) => ({
        ...(report && {
          ...report,
          createdOnFormatted: formatDate(report.createdOn || '', config.DATETIME.DATE_WITH_TIME_FORMAT_WITHOUT_AT),
          updatedOnFormatted: formatDate(report.updatedOn || '', config.DATETIME.DATE_WITH_TIME_FORMAT_WITHOUT_AT)
        })
      })),
      { skip, take }
    )

    return {
      data: data.filter(report => report.id),
      total: accessTokens?.totalCount || 0
    }
  }, [accessTokens, accessTokenTable])

  const pageConfig: BDSGridPagerConfig = useMemo(() => {
    const { skip, take }: { skip: number; take: number } = accessTokenTable

    return {
      pageable: {
        buttonCount: 5
      },
      skip,
      take,
      total: tableData.total,
      onPageChange: (e: any) => {
        dispatch(updateAccountsTable(e.page))

        if (!loadedAccessTokensOffsets?.includes(e.page.skip)) {
          dispatch(listAccessTokens({ product: accessTokenProduct }))
        }
      }
    }
  }, [accessTokenTable, tableData, dispatch, loadedAccessTokensOffsets])

  const sortConfig: BDSGridSortConfig | {} = useMemo(() => {
    if (!tableData.total) {
      return {}
    }

    return {
      sortable: !listAccessTokenInProgress && {
        allowUnsort: false,
        mode: 'single'
      },
      sort: accessTokenTable.sort,
      onSortChange: (e: any) => {
        updateTableData({ sort: e.sort })
      }
    }
  }, [accessTokenTable, updateTableData, tableData.total, listAccessTokenInProgress])

  const searchFieldConfig: SearchFieldProps = useMemo(() => {
    return {
      id: SEARCH_FIELD_ID,
      value: state.searchString,
      onChange: (e: any) => {
        const validatedSearchString = validateSearchString(e.target.value)

        if (accessTokenTable.search !== validatedSearchString) {
          debouncedOnChange(validatedSearchString)
          setState({ searchString: validatedSearchString })
        }
      },
      disabled: listAccessTokenInProgress
    }
  }, [state.searchString, accessTokenTable, debouncedOnChange, validateSearchString, listAccessTokenInProgress])

  return useMemo(() => {
    return [
      {
        isLoaded: !!accessTokens.data,
        inProgress: listAccessTokenInProgress,
        searchFieldConfig,
        tableData,
        pageConfig,
        sortConfig,
        columns: accessTokenTable.GRID_COLUMNS,
        columnsConfig: accessTokenTable.columnsConfig
      }
    ]
  }, [
    accessTokens.data,
    listAccessTokenInProgress,
    searchFieldConfig,
    tableData,
    pageConfig,
    sortConfig,
    accessTokenTable.GRID_COLUMNS,
    accessTokenTable.columnsConfig
  ])
}
